import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Col, Container, Row, Table, Button, Badge } from "react-bootstrap";
import UploadIcon from "../assets/images/upload_file_rgb_red.svg";
import "../assets/styles/dataImport.scss";
import api from "../services/httpClient";

const statusNames = {
  "-1": "Do akceptacji",
  "0": "Nowy",
  "1": "W trakcie wykonywania",
  "2": "Zakończony",
  "4": "Przerwany - Błąd",
  "5": "Odrzucony",
  "6": "Zaplanowany",
  "7": "Wycofany",
  "8": "W trakcie wycofywania",
  "9": "Wycofanie nieudane",
};

const actionNames = {
  "0": "Masowa aktualizacja",
  "3": "Import CSV",
  "4": "Import SHP",
};

const DataImportDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const claims = accounts?.[0]?.idTokenClaims || {};
  const rolesRaw = claims.roles || claims.role || claims.groups;
  const roles = Array.isArray(rolesRaw) ? rolesRaw : rolesRaw ? [rolesRaw] : [];
  const isSuperUser = roles.includes("ROLE_SUPERUSER");

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await api.get(`/HistoryBulk/${id}`);
        setDetails(response.data);
      } catch (error) {
        console.error("Error fetching import details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  if (loading) {
    return (
      <Container fluid className="mt-4">
        <p>Ładowanie...</p>
      </Container>
    );
  }

  if (!details) {
    return (
      <Container fluid className="mt-4">
        <p>Nie znaleziono importu o ID {id}.</p>
        <Button variant="secondary" onClick={() => navigate("/data-import")}>
          Powrót do listy
        </Button>
      </Container>
    );
  }

  // ROLE_USER nie ma dostępu do szczegółów Import CSV (action=3)
  if (!isSuperUser && details.action === 3) {
    return (
      <Container fluid className="mt-4">
        <p>Brak uprawnień do wyświetlenia szczegółów tego importu.</p>
        <Button variant="secondary" onClick={() => navigate("/data-import")}>
          Powrót do listy
        </Button>
      </Container>
    );
  }

  const parsedData = (() => {
    try { return JSON.parse(details.data); } catch { return null; }
  })();

  const parsedFilter = (() => {
    try { return JSON.parse(details.filter); } catch { return null; }
  })();

  // Czy uzytkownik moze modyfikowac ten wpis (odrzucic / edytowac daty).
  // Backend wylicza flage, bo frontend nie zna wlasnego UserId z bazy.
  // `!== false` zachowuje dotychczasowe zachowanie, gdy API nie zwroci pola.
  const canModify = details.canModify !== false;

  return (
    <div className="data-import-details-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img src={UploadIcon} alt="Data Import Icon" style={{ width: "30px", height: "30px" }} />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Szczegóły importu #{details.id}
            </h3>
          </Col>
          <Col xs="auto">
            <Button variant="secondary" onClick={() => navigate("/data-import")}>
              Powrót do listy
            </Button>
          </Col>
        </Row>

        <Row>
          {/* Lewa kolumna — metadane */}
          <Col md={8}>
            <h5>Metadane</h5>
            <Table className="data-import-meta-table" borderless>
              <tbody>
                <tr><td><strong>ID</strong></td><td>{details.id}</td></tr>
                <tr><td><strong>Typ</strong></td><td>{details.actionName || actionNames[details.action]}</td></tr>
                <tr>
                  <td><strong>Status</strong></td>
                  <td>
                    <Badge bg={getStatusColor(details.status)}>
                      {details.statusName || statusNames[details.status]}
                    </Badge>
                  </td>
                </tr>
                <tr><td><strong>Użytkownik</strong></td><td>{details.userName}</td></tr>
                <tr><td><strong>Zaakceptował</strong></td><td>{details.acceptUserName}</td></tr>
                <tr><td><strong>Data dodania</strong></td><td>{details.dateStart}</td></tr>
                {details.executionDate && <tr><td><strong>Data zaplanowana</strong></td><td>{details.executionDate}</td></tr>}
                {details.rollbackDate && <tr><td><strong>Data wycofania</strong></td><td>{details.rollbackDate}</td></tr>}
                {details.dateEnd && <tr><td><strong>Data zakończenia</strong></td><td>{details.dateEnd}</td></tr>}
                <tr><td><strong>Rekordów</strong></td><td>{details.countTotal}</td></tr>
                <tr><td><strong>Zmodyfikowanych</strong></td><td>{details.countDone}</td></tr>
                <tr><td><strong>Bez zmian</strong></td><td>{details.countSkipped}</td></tr>
                <tr><td><strong>Pozostało</strong></td><td>{details.countLeft}</td></tr>
                {details.fileName && <tr><td><strong>Plik</strong></td><td>{details.fileName}</td></tr>}
              </tbody>
            </Table>

            {parsedData && (
              <>
                <h5 className="mt-3">Wartości</h5>
                {parsedData.columns ? (
                  <>
                    <p className="mb-1"><strong>Rekordów ze zmianami:</strong> {parsedData.totalChanges ?? "—"}</p>
                    {parsedData.changedFields != null && (
                      <p className="mb-1"><strong>Zmienionych pól:</strong> {parsedData.changedFields}</p>
                    )}
                    <Table className="data-import-details-table" borderless>
                      <thead>
                        <tr><th>Kolumna</th><th>Liczba zmian</th><th>Zmiana na</th></tr>
                      </thead>
                      <tbody>
                        {Object.entries(parsedData.columns).map(([col, count]) => {
                          // Rozkład wartości może nie istnieć — rekordy sprzed wprowadzenia valueCounts
                          const dist = parsedData.valueCounts?.[col];
                          const topValues = dist?.topValues
                            ? Object.entries(dist.topValues)
                            : [];

                          return (
                            <tr key={col}>
                              <td>{col}</td>
                              <td>{count}</td>
                              <td>
                                {topValues.length > 0 ? (
                                  <>
                                    {topValues.map(([val, cnt]) => (
                                      <div key={val}>
                                        {val === ""
                                          ? <em className="text-muted">(wyczyszczenie)</em>
                                          : val}
                                        {" "}<Badge bg="secondary">{cnt}</Badge>
                                      </div>
                                    ))}
                                    {dist.otherValuesCount > 0 && (
                                      <div className="text-muted small mt-1">
                                        + {dist.otherValuesCount} innych wartości ({dist.otherChangesCount} zmian)
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </>
                ) : (
                  <Table className="data-import-details-table" borderless>
                    <thead>
                      <tr><th>Pole</th><th>Wartość</th></tr>
                    </thead>
                    <tbody>
                      {Object.entries(parsedData).map(([key, value]) => (
                        <tr key={key}>
                          <td>{key}</td>
                          <td>
                            {value && typeof value === "object"
                              ? JSON.stringify(value)
                              : String(value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>
            )}

            {parsedFilter && (
              <>
                <h5 className="mt-3">Filtr</h5>
                <Table className="data-import-details-table" borderless>
                  <thead>
                    <tr><th>Pole</th><th>Wartość</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(parsedFilter).map(([key, value]) => (
                      <tr key={key}><td>{key}</td><td>{String(value)}</td></tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Col>

          {/* Prawa kolumna — akcje */}
          <Col md={4}>
            <h5>Akcje</h5>
            <div className="d-grid gap-2">
              {/* canModify pochodzi z backendu — zwykly uzytkownik widzi wpisy calego
                  oddzialu, ale modyfikowac moze wylacznie wlasne.
                  Fallback `!== false` zachowuje zachowanie dla starszych odpowiedzi API. */}
              {canModify && (details.status === 6 || details.status === 2) && (
                <Button
                  variant="outline-primary"
                  onClick={() => navigate(`/data-import/${details.id}/edit-dates`)}
                >
                  Edytuj datę
                </Button>
              )}
              {/* Akceptacja jest wylacznie w gestii PowerUsera */}
              {isSuperUser && details.status === -1 && (
                <Button variant="success" onClick={() => handleAccept(details.id)}>
                  Akceptuj
                </Button>
              )}
              {canModify && (details.status === -1 || details.status === 6) && (
                <Button variant="danger" onClick={() => handleReject(details.id)}>
                  Odrzuć
                </Button>
              )}
              {isSuperUser && details.fileName && details.action === 3 && (
                <Button variant="outline-secondary" onClick={() => handleDownloadCsv(details.id)}>
                  Pobierz CSV
                </Button>
              )}

              {/* Komunikat dla użytkownika */}
              {!canModify && (
                <p className="text-muted small mb-0">
                  Import został utworzony przez innego użytkownika z Twojego oddziału —
                  możesz go podejrzeć, ale nie modyfikować.
                </p>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const handleAccept = async (id) => {
  try {
    await api.post(`/HistoryBulk/Accept`, { id: [id] });
    alert("Import został zaakceptowany.");
    window.location.reload();
  } catch (error) {
    alert(error.response?.data?.message || "Wystąpił błąd.");
  }
};

const handleReject = async (id) => {
  try {
    await api.post(`/HistoryBulk/Reject`, { id: [id] });
    alert("Import został odrzucony.");
    window.location.reload();
  } catch (error) {
    alert(error.response?.data?.message || "Wystąpił błąd.");
  }
};

const handleDownloadCsv = async (id) => {
  try {
    const response = await api.get(`/Import/GetCsv/${id}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    const contentDisposition = response.headers["content-disposition"];
    let fileName = `import_${id}.csv`;
    if (contentDisposition) {
      // Obsługa nagłówka content-disposition z formatem: filename="..."; filename*=UTF-8''...
      const match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i)
        || contentDisposition.match(/filename="?([^";]+)"?/i);
      if (match) {
        fileName = decodeURIComponent(match[1].trim());
      }
    }
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    const msg = error.response?.status === 404
      ? "Plik CSV nie zosta\u0142 znaleziony."
      : "Wyst\u0105pi\u0142 b\u0142\u0105d podczas pobierania pliku.";
    alert(msg);
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case -1: return "warning";
    case 0: return "info";
    case 1: return "primary";
    case 2: return "success";
    case 4: return "danger";
    case 5: return "secondary";
    case 6: return "info";
    case 7: return "dark";
    case 8: return "primary";
    case 9: return "danger";
    default: return "light";
  }
};

export default DataImportDetails;
